import { useCallback, useEffect, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { isEmbedMode } from './embed.js';

/**
 * Manages app-shell chrome preferences: color theme (dark/light) and
 * sidebar collapsed state. Persisted in localStorage so the user's
 * choice survives reloads.
 */

const STORAGE_KEY_THEME = 'canopy.chrome.theme';
const STORAGE_KEY_NAV = 'canopy.chrome.nav';
const STORAGE_KEY_LIST_PREFIX = 'canopy.chrome.listSidebar.';

export type ThemeMode = 'dark' | 'light';
export type NavMode = 'expanded' | 'collapsed';
export type ListSidebarMode = 'expanded' | 'collapsed';

function readInitial<T extends string>(key: string, fallback: T, valid: readonly T[]): T {
  try {
    const v = localStorage.getItem(key);
    return v && (valid as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

export function useAppChrome(): {
  theme: ThemeMode;
  nav: NavMode;
  toggleTheme(): void;
  toggleNav(): void;
  setTheme(mode: ThemeMode): void;
  setNav(mode: NavMode): void;
} {
  // When Sage is embedded in the Canopy portal (?embed=1), force the
  // light theme. The portal doesn't have a dark mode of its own; a
  // dark Sage inside a light portal frame reads as two unrelated
  // products, not one. Embedded mode overrides any prior localStorage
  // preference and ignores toggleTheme/setTheme calls below.
  const embedded = isEmbedMode();
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    embedded ? 'light' : readInitial<ThemeMode>(STORAGE_KEY_THEME, 'dark', ['dark', 'light']),
  );
  const [nav, setNavState] = useState<NavMode>(() =>
    readInitial<NavMode>(STORAGE_KEY_NAV, 'expanded', ['expanded', 'collapsed']),
  );

  // Apply theme to <html data-theme="…">. We mount on <html> so
  // `color-scheme` from tokens.css propagates to native controls.
  // In embed mode we also skip the localStorage write so a standalone
  // Sage tab in the same browser later picks up its own saved
  // preference instead of inheriting 'light' from the embed visit.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (!embedded) {
      try {
        localStorage.setItem(STORAGE_KEY_THEME, theme);
      } catch {
        /* */
      }
    }
  }, [theme, embedded]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_NAV, nav);
    } catch {
      /* */
    }
  }, [nav]);

  const toggleTheme = useCallback(() => {
    if (embedded) return; // Theme is locked to light in embed mode.
    setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));
  }, [embedded]);
  const toggleNav = useCallback(() => {
    setNavState((n) => (n === 'expanded' ? 'collapsed' : 'expanded'));
  }, []);

  return {
    theme,
    nav,
    toggleTheme,
    toggleNav,
    setTheme: embedded
      ? () => {
          /* locked to light in embed */
        }
      : setThemeState,
    setNav: setNavState,
  };
}

/**
 * Per-surface (Reports list, Insights list) sidebar state, persisted
 * to localStorage under a distinct key so different pages remember
 * their own preference independently.
 */
export function useListSidebar(surface: string): {
  mode: ListSidebarMode;
  toggle(): void;
  set(mode: ListSidebarMode): void;
} {
  const key = STORAGE_KEY_LIST_PREFIX + surface;
  const [mode, setMode] = useState<ListSidebarMode>(() =>
    readInitial<ListSidebarMode>(key, 'expanded', ['expanded', 'collapsed']),
  );
  useEffect(() => {
    try {
      localStorage.setItem(key, mode);
    } catch {
      /* */
    }
  }, [key, mode]);
  const toggle = useCallback(() => {
    setMode((m) => (m === 'expanded' ? 'collapsed' : 'expanded'));
  }, []);
  return { mode, toggle, set: setMode };
}

const STORAGE_KEY_LIST_WIDTH_PREFIX = 'canopy.chrome.listSidebarWidth.';
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 720;
const SIDEBAR_DEFAULT_WIDTH = 280;

/**
 * Width-state companion to useListSidebar. Returns the current pixel
 * width, a setter (clamped to MIN/MAX), and a `bind` callback that
 * lets a resizer <div> drive the width with a mousedown.
 *
 * Width is persisted per-surface so each page (templates, reports,
 * insights, …) remembers its own preferred width across reloads.
 */
export function useListSidebarWidth(surface: string): {
  width: number;
  setWidth(px: number): void;
  bindResizer(): { onPointerDown(e: ReactPointerEvent): void };
  /** Reset to the default. Useful after odd dragging. */
  reset(): void;
} {
  const key = STORAGE_KEY_LIST_WIDTH_PREFIX + surface;
  const [width, setWidthState] = useState<number>(() => {
    if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
    try {
      const raw = localStorage.getItem(key);
      const n = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(n) && n >= SIDEBAR_MIN_WIDTH && n <= SIDEBAR_MAX_WIDTH) {
        return n;
      }
    } catch {
      /* */
    }
    return SIDEBAR_DEFAULT_WIDTH;
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, String(width));
    } catch {
      /* */
    }
  }, [key, width]);

  const setWidth = useCallback((px: number) => {
    const clamped = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, Math.round(px)));
    setWidthState(clamped);
  }, []);

  const bindResizer = useCallback(
    () => ({
      // CSAGE-429: pointer events WITH capture instead of document-level
      // mouse listeners. The embed layout renders the dashboard in a
      // CROSS-ORIGIN iframe (api.sage kiosk view); releasing the button
      // over that iframe delivered mouseup to the iframe's document, the
      // parent never heard it, and the drag stayed latched to every
      // subsequent mouse move. setPointerCapture routes ALL pointer
      // events to the resizer element for the drag's duration, iframes
      // included; lostpointercapture is the safety net for tab
      // switches / capture theft.
      onPointerDown(e: ReactPointerEvent): void {
        if (e.button !== 0) return;
        e.preventDefault();
        const handle = e.currentTarget as HTMLElement;
        handle.setPointerCapture(e.pointerId);
        const startX = e.clientX;
        const startW = width;
        const layout = handle.closest('.split-layout');
        if (layout instanceof HTMLElement) {
          layout.dataset.resizeActive = '1';
        }
        function onMove(ev: PointerEvent): void {
          setWidth(startW + (ev.clientX - startX));
        }
        function end(): void {
          handle.removeEventListener('pointermove', onMove);
          handle.removeEventListener('pointerup', end);
          handle.removeEventListener('lostpointercapture', end);
          try {
            handle.releasePointerCapture(e.pointerId);
          } catch {
            /* already released */
          }
          document.body.style.removeProperty('cursor');
          document.body.style.removeProperty('user-select');
          if (layout instanceof HTMLElement) {
            delete layout.dataset.resizeActive;
          }
        }
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', end);
        handle.addEventListener('lostpointercapture', end);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
      },
    }),
    [width, setWidth],
  );

  const reset = useCallback(() => setWidthState(SIDEBAR_DEFAULT_WIDTH), []);

  return { width, setWidth, bindResizer, reset };
}
